import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loader from '../../common/Loader';
import { AuthContext } from '../../../context/AuthContext';
import { fetchFinancialData, fetchWalletData, makeApiRequest, sendWithdrawalEmail } from '../../../api/apiService';
import InputField from '../../common/InputField';
import { Button } from '../../common/Button';

const WithdrawalScreen = () => {
  const { user } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [mergedOptions, setMergedOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [accountName, setAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [swiftBic, setSwiftBic] = useState('');
  const [iban, setIban] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null); // New state for error messages

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountParam = queryParams.get('account');
  const walletParam = queryParams.get('wallet');

  // Get current balance of selected account
  const getCurrentBalance = () => {
    if (!selectedAccount) return 0;
    const selected = mergedOptions.find(item => item.id === selectedAccount);
    return selected ? parseFloat(selected.balance) : 0;
  };

  // Fetch account and wallet data (refresh when sidebar query params change)
  useEffect(() => {
    if (user) {
      const { access_token, user_id } = user;

      fetchFinancialData(access_token, user_id).then((response) => {
        if (!response.error && response.data.trading_accounts) {
          const realAccounts = response.data.trading_accounts.filter(
            trading_accounts => trading_accounts.account_mode !== "Demo"
          );
          setAccounts(realAccounts);
        }
      });

      fetchWalletData(access_token, user_id).then((response) => {
        if (!response.error && response.data.wallets) {
          setWallets(response.data.wallets);
        }
      });
    }
  }, [user, location.search]);

  // Merge accounts and wallets
  useEffect(() => {
    const combined = [
      ...accounts.map((account) => ({
        id: account.account_id,
        balance: account.balance,
        currency: account.currency || 'USD',
        type: 'account',
      })),
      ...wallets.map((wallet) => ({
        id: wallet.wallet_number,
        balance: wallet.balance,
        currency: wallet.currency,
        type: 'wallet',
      })),
    ];
    setMergedOptions(combined);
  }, [accounts, wallets]);

  // Set default account from URL params + reset form so each navigation loads "fresh"
  useEffect(() => {
    // reset step-specific state
    setStatus(null);
    setError(null);
    setAmount('');
    setAccountName('');
    setBankName('');
    setBranchName('');
    setSwiftBic('');
    setIban('');

    if (accountParam) {
      setSelectedAccount(accountParam);
    } else if (walletParam) {
      setSelectedAccount(walletParam);
    } else {
      setSelectedAccount('');
    }
  }, [accountParam, walletParam]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous errors
    setError(null);

    // Validate all fields are filled
    if (!selectedAccount || !amount || !accountName || !bankName || !branchName || !swiftBic || !iban) {
      setError('Please fill all fields.');
      return;
    }

    // Validate amount is positive number
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid positive amount.');
      return;
    }

    // Validate amount doesn't exceed balance
    const currentBalance = getCurrentBalance();
    if (amountNum > currentBalance) {
      setError(`Insufficient funds. Your current balance is ${currentBalance} ${currency}.`);
      return;
    }

    setIsLoading(true);
    setStatus(null);

    const withdrawalData = {
      userId: user.user_id,
      selectedAccount,
      amount,
      accountName,
      bankName,
      branchName,
      swiftBic,
      iban,
      date: new Date().toISOString(),
    };

    try {
      const response = await sendWithdrawalEmail(user.access_token, withdrawalData);
      if (!response.error) {
        setStatus('success');
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus('failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="p-5">
        <div className="bg-white shadow-lg rounded-2xl p-6 w-full">
          <h1 className="text-2xl font-bold mb-4 text-gray-800">Withdrawal</h1>

          {/* Display current balance if account is selected */}
          {selectedAccount && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-800 font-medium">
                Available Balance: {getCurrentBalance()} {currency}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Select Account/Wallet */}
            <div className="mb-4">
              <label htmlFor="accountSelect" className="block text-text text-m font-bold mb-2">
                Select Account:
              </label>
              <select
                id="accountSelect"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                style={{ height: '43px' }}
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                required
              >
                <option value="">Select an account/wallet</option>
                {mergedOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.type === 'account' ? 'Account' : 'Wallet'} - {item.id} (Balance: {item.balance} {item.currency})
                  </option>
                ))}
              </select>
            </div>

            {/* Withdrawal Amount */}
            <div className="mb-4">
              <label htmlFor="amount" className="block text-text text-m font-bold mb-2">
                Withdrawal Amount:
              </label>
              <InputField
                type="number"
                id="amount"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                min="0.01"
                step="0.01"
              />
            </div>

            {/* Currency */}
            <div className="mb-4">
              <label htmlFor="currency" className="block text-text text-m font-bold mb-2">
                Currency:
              </label>
              <select
                id="currency"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                required
              >
                <option value="USD">USD</option>
              </select>
            </div>

            {/* Bank Details */}
            <div className="col-span-1 sm:col-span-2">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Bank Details</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="accountName" className="block text-text text-m font-bold mb-2">
                    Name of Account Holder:
                  </label>
                  <InputField
                    type="text"
                    id="accountName"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="bankName" className="block text-text text-m font-bold mb-2">
                    Bank Name:
                  </label>
                  <InputField
                    type="text"
                    id="bankName"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="branchName" className="block text-text text-m font-bold mb-2">
                    Branch Name:
                  </label>
                  <InputField
                    type="text"
                    id="branchName"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                    value={branchName}
                    onChange={(e) => setBranchName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="swiftBic" className="block text-text text-m font-bold mb-2">
                    SWIFT/BIC:
                  </label>
                  <InputField
                    type="text"
                    id="swiftBic"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                    value={swiftBic}
                    onChange={(e) => setSwiftBic(e.target.value)}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="iban" className="block text-text text-m font-bold mb-2">
                    Account Number / IBAN:
                  </label>
                  <InputField
                    type="text"
                    id="iban"
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-red-400"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="w-full flex justify-start">
              <Button
                type="submit"
                width="w-64"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Submit'}
              </Button>
            </div>
          </form>

          {/* Error Modal */}
          {error && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-xl font-bold mb-4 text-red-600">Error</h2>
                <p className="mb-4">{error}</p>
                <button
                  className="w-full bg-red-500 text-white p-2 rounded hover:bg-red-600 transition"
                  onClick={() => setError(null)}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Success/Failure Modal */}
          {status && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">
                  {status === 'success' ? 'Success!' : 'Failed!'}
                </h2>
                <p className="mb-4">
                  {status === 'success'
                    ? 'Your withdrawal request was submitted successfully.'
                    : 'Your withdrawal request failed. Please try again.'}
                </p>
                <button
                  className="w-full bg-red-500 text-white p-2 rounded hover:bg-red-600 transition"
                  onClick={() => setStatus(null)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WithdrawalScreen;